import { useRequiredContext } from "@redotech/react-util/context";
import { BillingType } from "@redotech/redo-billing-common/billing-window-config-definition";
import { Team } from "@redotech/redo-team-common/team";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { DateInput } from "@redotech/redo-web/date-picker";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { getDateStartOfDayInTimezone } from "@redotech/util/date";
import { enqueueSnackbar } from "notistack";
import { memo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { RedoAdminClientContext } from "../../../client/context";
import { getTeam } from "../../../client/team";
import { LoadTeamContext } from "../../team";

const plans = {
  [BillingType.MARKETING_SMS]: {
    name: "SMS",
    savedStartDate: (team: Team) =>
      team.settings.marketing?.smsBilling?.startDate,
  },
  [BillingType.MARKETING_EMAIL]: {
    name: "email",
    savedStartDate: (team: Team) =>
      team.settings.marketing?.emailBilling?.startDate,
  },
  [BillingType.RECOVER]: {
    name: "Recover",
    savedStartDate: (team: Team) => team.settings.recover?.billing?.startDate,
  },
  [BillingType.ORDER_TRACKING]: {
    name: "order tracking",
    savedStartDate: (team: Team) =>
      team.settings.orderTracking?.billing?.invoiceBilling?.startDate,
  },
};

export const RestartBillingPlanModal = memo(function RestartBillingPlanModal({
  billingType,
  teamId,
  teamTimezone,
  onClose,
  onStartDateSaved,
}: {
  billingType: keyof typeof plans;
  teamId: string;
  teamTimezone: string;
  onClose: () => void;
  onStartDateSaved: (startDate: Date) => void;
}) {
  const client = useRequiredContext(RedoAdminClientContext);
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const loadTeam = useRequiredContext(LoadTeamContext);
  const [date, setDate] = useState<Date | null>(null);
  // react-date-picker clears a half-typed date whenever minDate changes identity.
  const [minDate] = useState(() => new Date());
  const plan = plans[billingType];
  const planName = plan.name;

  const startDate = date && getDateStartOfDayInTimezone(date, teamTimezone);
  const today = getDateStartOfDayInTimezone(new Date(), teamTimezone);
  const isFuture = !!startDate && startDate.getTime() > today.getTime();

  const restart = async () => {
    if (!startDate) {
      return;
    }
    try {
      const { deletedBillCount } = await rpcClient.restartBillingPlan({
        teamId,
        billingType,
        startDate,
      });
      enqueueSnackbar(
        `${planName} plan restarts ${startDate.toLocaleDateString("en-US", { timeZone: teamTimezone })}; deleted ${deletedBillCount} $0 bill${deletedBillCount === 1 ? "" : "s"}`,
        { variant: "success" },
      );
      onStartDateSaved(startDate);
      onClose();
      loadTeam();
    } catch (error) {
      enqueueSnackbar(
        `Could not restart the ${planName} plan: ${error instanceof Error ? error.message : String(error)}`,
        { variant: "error" },
      );
      // The server may have saved the new date before failing. Sync the form, or
      // the page's Save writes the old date back over a plan with no bills.
      const saved = plan.savedStartDate(await getTeam(client, { teamId }));
      if (saved) {
        onStartDateSaved(new Date(saved));
      }
      loadTeam();
    }
  };

  return (
    <RedoModal
      isOpen
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Restart plan",
        onClick: restart,
        disabled: !isFuture,
        theme: "destructive",
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      subtitle={`Deletes this plan's ${planName} bills and starts a new plan on the date you pick, charging its first period fee that day. Only works while every ${planName} bill so far is $0 and unpaid.`}
      theme="warn"
      title={`Restart ${planName} plan on a new date?`}
    >
      <LabeledInput label="New start date">
        <DateInput minDate={minDate} value={date} valueChange={setDate} />
      </LabeledInput>
    </RedoModal>
  );
});
